import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { QrCode, LogOut, Menu, X } from "lucide-react";
import { useGetMe, getGetMeQueryKey } from "@workspace/api-client-react";
import { useState } from "react";
import { useTheme } from "../theme/ThemeProvider";

export function Navbar() {
  const [location] = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { data: user } = useGetMe({
    query: { queryKey: getGetMeQueryKey(), retry: false, refetchOnWindowFocus: false }
  });
  
  const [showMenu, setShowMenu] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem("commonqr_token");
    window.location.href = "/";
  };

  const scrollToPricing = () => {
    if (location === "/") {
      const el = document.getElementById("pricing");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = "/#pricing";
    }
  };

  return (
    <nav className="sticky top-0 z-50 w-full bg-card/90 backdrop-blur-xl border-b border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">
          <Link href="/" className="flex items-center gap-2">
            <QrCode size={22} className="text-orange-500" strokeWidth={2} />
            <span className="font-semibold text-[17px] tracking-tight text-foreground">CommonQR</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <button onClick={scrollToPricing} className="text-[13px] text-foreground transition-colors">
              Pricing
            </button>
            <Link href="/demos" className="text-[13px] text-foreground transition-colors">
              Demos
            </Link>
            <Link href="/themes" className="text-[13px] text-foreground transition-colors">
              Themes
            </Link>

            <div className="h-4 w-px bg-border mx-1" />

            <button
              type="button"
              onClick={toggleTheme}
              className="flex h-9 w-9 items-center justify-center rounded-full text-lg text-foreground hover:bg-accent/10"
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              {theme === "dark" ? "☀️" : "🌙"}
            </button>

            {user ? (
              <>
                <Link href="/dashboard" className="text-[13px] text-foreground transition-colors">
                  Dashboard
                </Link>
                <Button variant="ghost" size="sm" onClick={handleLogout} className="text-foreground hover:text-red-600 text-[13px]">
                  <LogOut className="w-4 h-4 mr-1.5" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link href="/login" className="text-[15px] text-foreground transition-colors">
                  Log in
                </Link>
                <Link href="/signup" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full text-sm font-medium transition-colors">
                  Create QR
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button onClick={() => setShowMenu(!showMenu)} className="p-2 text-foreground">
              {showMenu ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Dropdown */}
      {showMenu && (
        <div className="md:hidden absolute top-14 left-0 w-full bg-card/95 backdrop-blur-xl border-b border-border py-4 px-6 flex flex-col gap-4 shadow-xl">
          <button onClick={() => { setShowMenu(false); scrollToPricing(); }} className="text-left text-[15px] text-foreground">
            Pricing
          </button>
          <Link href="/demos" onClick={() => setShowMenu(false)} className="text-[15px] text-foreground">
            Demos
          </Link>
          <Link href="/themes" onClick={() => setShowMenu(false)} className="text-[15px] text-foreground">
            Themes
          </Link>

          <div className="h-px w-full bg-border my-1" />

          {user ? (
            <>
              <Link href="/dashboard" onClick={() => setShowMenu(false)} className="text-[15px] text-foreground">
                Dashboard
              </Link>
              <button onClick={handleLogout} className="text-left text-[15px] text-red-600">
                Logout
              </button>
            </>
          ) : (
            <>
              <Link href="/login" onClick={() => setShowMenu(false)} className="text-[15px] text-foreground">
                Log in
              </Link>
              <Link href="/signup" onClick={() => setShowMenu(false)} className="w-full text-center py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full text-sm font-medium transition-colors">
                Create QR
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
